import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Progress,
  Typography,
  Badge,
  Flex,
  Tooltip,
  Modal,
  Checkbox,
  Tag,
  Tabs
} from 'antd';
import BatteryGauge from 'react-battery-gauge';
import { apiGetDevicesInformation } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';
import { communityList, downloadBlobFile, formatCurrency } from '@utils/utils';
import StatCards from './StatCards';
import {
  SearchOutlined,
  ClearOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import { Link } from 'react-router-dom';
import TabPane from 'antd/es/tabs/TabPane';
import { URL_API } from '@configs/index';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import WarningImage from '@assets/images/warning.png';
import OnOffImage from '@assets/images/on-off.png';
import NbIOTImage from '@assets/images/nb-iot.png';
import Loading from '@components/Loading';
import { usePermission } from '@contexts/Auth/UsePermission';
import FilterAdvanceDrawer from '@components/FilterAdvance';

const { Option } = Select;
const { Title, Paragraph } = Typography;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('HP');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [countDetail, setCountDetail] = useState(null);
  const [custId, setCustId] = useState(null);
  const [status, setStatus] = useState('all');
  const [filterWarning, setFilterWarning] = useState(0);
  const [filterRSRP, setFilterRSRP] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const { dashboard } = usePermission();
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});

  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => {
    setIsOpen(true);
  };

  const onDrawerClose = () => {
    setIsOpen(false);
  };

  const fetchData = async () => {
    try {
      // setIsFetching(true);
      const res = await apiGetDevicesInformation({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() ?? undefined,
        customerId: custId ?? undefined,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        status: ['online', 'offline']?.includes(status) ? status : undefined,
        warning: ['warning']?.includes(status) ? true : undefined,
        rsrp: ['RSRP_STRONG', 'RSRP_NORMAL', 'RSRP_LOW']?.includes(status) ? status : undefined,
        ...dataFilterAdvance
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
      setCountDetail(res.data?.meta?.countDetail);
    } catch (err) {
      showError('Không lấy được dữ liệu!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 20000);

    return () => clearInterval(interval);
  }, [search, selectedCommunity, currentPage, custId, pageSize, status, dataFilterAdvance]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const WarningCell = ({ record }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const info = JSON.parse(record['/82/0']);
    const magneticAttackStatus = info?.magneticAttackStatus; // Cảnh báo đang bị tấn công từ tính
    const antiDemolitionStatus = info?.antiDemolition; // Cảnh báo đang bị tấn công tháo rời
    const happenedMagneticAttackStatus = info?.happenedMagneticAttack; // Cảnh báo đã bị tấn công từ tính
    const happenedAntiDemolitionStatus = info?.happenedAntiDemolition; // Cảnh báo đã bị tấn công tháo rời
    // const overFlowStatus = info?.overFlowStatus; // Cảnh báo dòng chảy đang vượt ngưỡng
    // const stopStatus = info?.stopStatus; // Cảnh báo kẹt công tơ
    const denseDataDiffirenceOfTheFlowPulseNumber =
      record?.denseData_diffirenceOfTheFlowPulseNumber;
    const reverseFlows = denseDataDiffirenceOfTheFlowPulseNumber?.filter(
      (item) => item < 0
    )?.length; // Cảnh báo dòng chảy ngược

    const showModal = () => {
      setIsModalVisible(true);
    };

    const handleOk = () => {
      setIsModalVisible(false);
    };

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const highlightStyle = (checked) => ({
      fontWeight: checked ? 'bold' : 'normal'
    });

    return (
      <>
        {antiDemolitionStatus ||
        happenedAntiDemolitionStatus ||
        magneticAttackStatus ||
        happenedMagneticAttackStatus ||
        reverseFlows ? (
          <>
            <div style={{ color: '#e11d48', fontWeight: 'bold' }}>
              <ExclamationCircleOutlined /> Có cảnh báo{' '}
              <Button type="link" onClick={showModal}>
                Xem chi tiết
              </Button>
            </div>
            <Modal
              title="Chi tiết cảnh báo"
              visible={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}>
              <Checkbox checked={magneticAttackStatus} style={highlightStyle(magneticAttackStatus)}>
                Cảnh báo đang bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedMagneticAttackStatus}
                style={highlightStyle(happenedMagneticAttackStatus)}>
                Cảnh báo đã bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox checked={antiDemolitionStatus} style={highlightStyle(antiDemolitionStatus)}>
                Cảnh báo đang bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedAntiDemolitionStatus}
                style={highlightStyle(happenedAntiDemolitionStatus)}>
                Cảnh báo đã bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox checked={reverseFlows} style={highlightStyle(reverseFlows)}>
                Cảnh báo dòng chảy ngược
              </Checkbox>
            </Modal>
          </>
        ) : (
          <div>-</div>
        )}
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 60,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: '/84/0',
        render: (text, record) => {
          let info = JSON.parse(record['/84/0']);
          if (!info) {
            return (
              <div style={{ color: '#e11d48', fontWeight: 'bold' }}>
                <Badge
                  status="error"
                  text="OFFLINE"
                  style={{ color: '#e11d48', fontSize: '18px' }}
                  size="default "
                />
              </div>
            );
          } else {
            const isOnline =
              (Number(Date.now()) - Number(record.last_seen_at)) / 1000 <=
              Number(info?.reportPeriod) + 60;
            return (
              <div style={{ color: isOnline ? 'green' : 'red', fontWeight: 'bold' }}>
                {isOnline ? (
                  <Badge
                    status="success"
                    text="ONLINE"
                    style={{ color: '#10b981', fontSize: '18px' }}
                    size="default "
                  />
                ) : (
                  <Badge
                    status="error"
                    text="OFFLINE"
                    style={{ color: '#e11d48', fontSize: '18px' }}
                    size="default "
                  />
                )}
              </div>
            );
          }
        },
        width: 100
      },
      {
        title: 'Thiết bị',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 180,
        align: 'center',
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'var(--link)' }}>
            {`SN:${record.serial_no}/ ${record.model}`}
          </Link>
        )
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        align: 'center',
        width: 100,
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'var(--link)' }}>
            {`${record.customer_id}`}
          </Link>
        )
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        align: 'center',
        width: 180,
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'var(--link)' }}>
            {`${record.customer_name}`}
          </Link>
        )
      },
      {
        title: 'CN',
        dataIndex: 'branch',
        align: 'center',
        width: 120
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        align: 'center',
        width: 70
      },
      {
        title: 'DHK',
        dataIndex: 'parent_device',
        align: 'center',
        width: 70
      },
      {
        title: 'Tuyến',
        dataIndex: 'route',
        align: 'center',
        width: 70
      },
      {
        title: 'Cỡ ĐH',
        dataIndex: 'size',
        align: 'center',
        width: 80
      },
      {
        title: 'Lần truyền cuối',
        dataIndex: 'last_seen_at',
        align: 'center',
        width: 110,
        render: (text, record) => {
          if (!record.last_seen_at) return <div>-</div>;
          return (
            <div>
              {new Intl.DateTimeFormat('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }).format(record.last_seen_at)}
            </div>
          );
        }
      },
      {
        title: (
          <Tooltip title="Cường độ sóng (dBm)">
            <span> Cường độ sóng (dBm)</span>
          </Tooltip>
        ),
        dataIndex: '/99/0',
        align: 'center',
        render: (text, record) => {
          let info = JSON.parse(record['/99/0']);
          if (!info) return <div>-</div>;
          let rsrp = info.rsrp / 10;
          let color = rsrp >= -100 ? '#10b981' : rsrp >= -110 ? '#f59e0b' : '#e11d48';
          let progress =
            rsrp >= -100
              ? (rsrp + 100) / 3 + 80
              : rsrp >= -110
              ? 3 * (rsrp + 110) + 50
              : rsrp + 160;
          return (
            <Flex gap="small" vertical>
              <Progress
                percentPosition={{ align: 'start', type: 'inner' }}
                size={[100, 20]}
                strokeColor={color}
                percent={progress}
                showInfo
                format={() => `${rsrp.toString()}`}
              />
            </Flex>
          );
        },
        width: 100
      },
      // {
      //   title: (
      //     <Tooltip title="Tỷ lệ tín/tạp (dB)">
      //       <span
      //         style={{
      //           whiteSpace: 'nowrap',
      //           overflow: 'hidden',
      //           textOverflow: 'ellipsis',
      //           maxWidth: 100,
      //           display: 'inline-block'
      //         }}>
      //         Tỷ lệ tín/tạp (dB)
      //       </span>
      //     </Tooltip>
      //   ),
      //   dataIndex: '/99/0',
      //   render: (text, record) => {
      //     let info = JSON.parse(record['/99/0']);
      //     if (!info) return <div>-</div>;
      //     let snr = (info.snr / 10).toFixed(2);
      //     return <div>{snr}</div>;
      //   },
      //   width: 100
      // },
      {
        title: 'Pin (%)',
        dataIndex: 'voltage',
        align: 'center',
        render: (text, record) => {
          if (!record.voltage) return <div>-</div>;

          /* Công thức cũ:
          const installTime = new Date(record.created_at).getTime();
          const lastSeenAt = record.last_seen_at;
          const timeDifference = lastSeenAt - installTime;
          let remainingBatCap =
            ((8500 * 1000 -
              (timeDifference * 30) / (3600 * 1000) -
              record.number_of_updates * 1394.52) /
              (8500 * 1000)) *
            100;
          if (isNaN(remainingBatCap)) remainingBatCap = 0;
          remainingBatCap = Math.max(0, Math.min(100, remainingBatCap));
          */

          const initialBattery = 8500 * 1000;
          const startedAt = new Date(record.install_time || record.created_at).getTime();
          const lastSeenAt = Number(record.last_seen_at);
          const operatingTimeMs =
            Number.isFinite(startedAt) && Number.isFinite(lastSeenAt)
              ? Math.max(0, lastSeenAt - startedAt)
              : 0;
          const numberOfUpdates = Math.max(0, Number(record.number_of_updates) || 0);
          const timeConsumption = (operatingTimeMs * 30) / (3600 * 1000);
          const transmissionConsumption = numberOfUpdates * 1394.52;
          const remainingBattery = initialBattery - timeConsumption - transmissionConsumption;
          const remainingBatCap = Math.max(
            0,
            Math.min(100, (remainingBattery / initialBattery) * 100)
          );

          return (
            <div>
              <BatteryGauge
                value={remainingBatCap}
                size={50}
                customization={{ readingText: { fontSize: 24 } }}
              />
            </div>
          );
        },
        width: 100
      },
      {
        title: (
          <Tooltip title="Chỉ số đồng hồ (m3)">
            <span> Chỉ số đồng hồ (m3)</span>
          </Tooltip>
        ),
        dataIndex: 'meter_reading',
        align: 'center',
        render: (text, record) => <div>{formatCurrency(record.meter_reading / 1000.0, 3)}</div>,
        width: 120
      },
      {
        title: (
          <Tooltip title="Trạng thái cảnh báo">
            <span> Trạng thái cảnh báo </span>
          </Tooltip>
        ),
        dataIndex: '/82/0',
        align: 'center',
        render: (text, record) => <WarningCell record={record} />,
        width: 120
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleCustIdChange = (e) => {
    setCustId(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/devices/export-dashboard-report`,
        'Thống kê chung.xlsx',
        {
          keyWord: search?.trim() ?? undefined,
          customerId: custId ?? undefined,
          community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
          status: status === 'all' ? undefined : status,
          warning: filterWarning == 1 ? 1 : undefined,
          rsrp: filterRSRP ?? undefined,
          ...dataFilterAdvance
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSearch('');
    setCustId('');
    setCurrentPage(1);
    setStatus('all');
    setFilterWarning(0);
    setFilterRSRP('');
    setDataFilterAdvance({});
    setFlagSearch(!flagSearch);
  };

  return (
    <div className="dashboard">
      <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
        <Col md={9} xs={24}>
          <Card
            style={{
              backgroundColor: '#eef1f6',
              height: '100%'
            }}
            bodyStyle={{ padding: '8px' }}>
            <Row gutter={[16, 16]}>
              <Col md={24} xs={24}>
                <SelectCommunity value={selectedCommunity} onChange={setSelectedCommunity} />
              </Col>
              <Col md={4} xs={4}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={openDrawer}
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    width: '100%'
                  }}
                />
              </Col>
              <Col md={10} xs={10}>
                <Input
                  value={search}
                  onChange={handleSearchChange}
                  onPressEnter={handleSearch}
                  placeholder="Tìm kiếm số serial, imei..."
                  style={{ width: '100%', height: '36px' }}
                />
              </Col>
              <Col md={10} xs={10}>
                <Input
                  value={custId}
                  onChange={handleCustIdChange}
                  onPressEnter={handleSearch}
                  placeholder="Tìm kiếm tên, mã KH..."
                  style={{ width: '100%', height: '36px' }}
                />
              </Col>
              <Col md={12} xs={12}>
                <Button
                  onClick={handleSearch}
                  icon={<SearchOutlined />}
                  style={{
                    backgroundColor: '#0284c7',
                    borderColor: '#0284c7',
                    color: '#fff',
                    height: '36px',
                    width: '100%'
                  }}>
                  Tìm kiếm
                </Button>
              </Col>
              <Col md={12} xs={12}>
                <Button
                  style={{
                    border: 'none',
                    backgroundColor: '#f1f5f9',
                    width: '100%',
                    height: '36px'
                  }}
                  icon={<ClearOutlined />}
                  onClick={clearFilter}>
                  Xoá bộ lọc
                </Button>
              </Col>
              {dashboard.export && (
                <Col md={24} xs={24}>
                  <Button
                    color="primary"
                    variant="filled"
                    loading={loading}
                    onClick={handleExportExcelButtonClick}
                    icon={<DownloadOutlined />}
                    style={{
                      backgroundColor: '#FFF',
                      borderColor: '#eef1f6',
                      color: '#0284c7',
                      height: '36px',
                      fontWeight: '500',
                      width: '100%'
                    }}>
                    Xuất Excel
                  </Button>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
        <Col md={15} xs={24}>
          <Card
            style={{
              backgroundColor: '#eef1f6'
            }}
            bodyStyle={{ padding: '8px' }}>
            {countDetail ? (
              <Row gutter={[16, 16]}>
                {/* First Chart */}
                <Col md={8} xs={8}>
                  <div style={{ textAlign: 'left', width: '100%' }}>
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <Chart
                        options={{
                          colors: ['#10b981', '#e11d48'],
                          labels: [
                            `Online: ${countDetail?.total_online}`,
                            `Offline: ${countDetail?.total_offline}`
                          ],
                          legend: {
                            position: 'bottom',
                            horizontalAlign: 'center',
                            fontWeight: '500',
                            fontSize: '12px'
                            // onItemClick: {
                            //   toggleDataSeries: false // Prevent toggling the series visibility
                            // }
                          },
                          tooltip: {
                            y: {
                              formatter: (val) => `${formatCurrency(val)}`
                            }
                          },
                          responsive: [
                            {
                              breakpoint: 480,
                              options: {
                                legend: {
                                  position: 'bottom'
                                }
                              }
                            }
                          ],
                          chart: {
                            events: {
                              dataPointSelection: (event, chartContext, config) => {
                                // Check which segment was clicked
                                const clickedIndex = config?.dataPointIndex;
                                if (clickedIndex === 0) {
                                  setStatus('online'); // Set the active tab to "Online"
                                } else if (clickedIndex === 1) {
                                  setStatus('offline'); // Set the active tab to "Offline"
                                }
                              }
                            }
                          }
                        }}
                        series={[
                          Number(countDetail?.total_online),
                          Number(countDetail?.total_offline)
                        ]}
                        height={200}
                        type="donut"
                      />
                      <img
                        src={OnOffImage}
                        alt="NB Logo"
                        style={{
                          position: 'absolute',
                          top: '40%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '70px',
                          height: '70px'
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={8} xs={8}>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    {/* Title */}

                    {/* Image */}
                    <img
                      src={WarningImage}
                      alt="Cảnh báo"
                      style={{ width: '150px', height: '150px', marginBottom: '10px' }}
                    />

                    {/* Label */}
                    <Typography
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#FF0000', // Red color for warnings,
                        cursor: 'pointer'
                      }}
                      onClick={() => setStatus('warning')}>
                      Có cảnh báo: {countDetail?.alarm || 0}{' '}
                      {/* Replace with actual warning count */}
                    </Typography>
                  </div>
                </Col>

                {/* Second Chart */}
                <Col md={8} xs={8}>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <div style={{ position: 'relative' }}>
                      <Chart
                        options={{
                          colors: ['#10b981', '#f59e0b', '#e11d48'],
                          labels: [
                            `Tốt: ${countDetail?.rsrp_strong}`,
                            `Trung bình: ${countDetail?.rsrp_normal}`,
                            `Yếu: ${countDetail?.rsrp_low}`
                          ],
                          legend: {
                            position: 'bottom'
                          },
                          chart: {
                            events: {
                              dataPointSelection: (event, chartContext, config) => {
                                // Check which segment was clicked
                                const clickedIndex = config?.dataPointIndex;
                                if (clickedIndex === 0) {
                                  setStatus('RSRP_STRONG'); // Set the active tab to "RSRP_STRONG"
                                } else if (clickedIndex === 1) {
                                  setStatus('RSRP_NORMAL');
                                } else if (clickedIndex === 2) {
                                  setStatus('RSRP_LOW');
                                }
                              }
                            }
                          }
                        }}
                        series={[
                          countDetail?.rsrp_strong,
                          countDetail?.rsrp_normal,
                          countDetail?.rsrp_low
                        ]}
                        height={200}
                        type="donut"
                      />
                      <img
                        src={NbIOTImage}
                        alt="NB Logo"
                        style={{
                          position: 'absolute',
                          top: '40%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '50px',
                          height: '40px'
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <div style={{ height: '218px' }}>
                <Loading />
              </div>
            )}
          </Card>
        </Col>
      </Row>
      {/* <Card> */}
      <Tabs
        activeKey={status}
        defaultActiveKey="all"
        onChange={(value) => {
          setCurrentPage(1);
          setStatus(value);
        }}>
        <TabPane tab={`Tất cả`} key={'all'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị Online`} key={'online'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị rớt mạng`} key={'offline'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị có cảnh báo`} key={'warning'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị sóng tốt`} key={'RSRP_STRONG'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị sóng trung bình`} key={'RSRP_NORMAL'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
        <TabPane tab={`Thiết bị sóng yếu`} key={'RSRP_LOW'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 8,
              thead: { backgroundColor: '#e2e8f0', color: '#64748b' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane>
      </Tabs>
      {/* </Card> */}
      <FilterAdvanceDrawer
        isOpen={isOpen}
        onDrawerClose={onDrawerClose}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
}
